/**
 * Dashboard Page - Two-Panel Layout
 * Left panel: inline metrics strip + activity feed
 * Right panel: team list + quick actions
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
  Alert,
  Skeleton,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Groups as TeamsIcon,
  SmartToy as BotIcon,
  Warning as EscalationIcon,
  Pending as PendingIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import {
  WelcomeHeader,
  MetricsStrip,
  ActivityFeed,
  QuickActionsGrid,
} from '@/components/dashboard';
import { teamApi, activityApi, adminApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: teams, isLoading: teamsLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamApi.list({ is_active: true, limit: 100 }).then((res) => res.data.items),
  });

  const { data: activitySummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['activity-summary'],
    queryFn: () => activityApi.getSummary(7).then((res) => res.data),
  });

  const { data: approvalStats, isLoading: approvalLoading } = useQuery({
    queryKey: ['approval-stats'],
    queryFn: () => adminApi.getStats().then((res) => res.data),
    enabled: !!user?.isAdmin,
  });

  const activeTeams = teams?.filter((t) => t.approval_status === 'approved') || [];
  const pendingTeams = teams?.filter((t) => t.approval_status === 'pending') || [];
  const statsLoading = teamsLoading || summaryLoading || (user?.isAdmin && approvalLoading);
  const displayTeams = activeTeams.slice(0, 8);

  return (
    <Box>
      <WelcomeHeader
        subtitle={
          activeTeams.length > 0
            ? `${activeTeams.length} active team${activeTeams.length > 1 ? 's' : ''} across your workspace`
            : 'Get started by creating or joining a team'
        }
      />

      {/* Alerts */}
      {pendingTeams.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {pendingTeams.length} team{pendingTeams.length > 1 ? 's' : ''} pending approval
        </Alert>
      )}

      {!teamsLoading && activeTeams.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You don't have any active teams yet. Create a new team or wait for your pending teams to
          be approved.
        </Alert>
      )}

      {/* Two-Panel Layout */}
      <Grid container spacing={2}>
        {/* LEFT PANEL: Metrics + Activity */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <MetricsStrip
                loading={!!statsLoading}
                items={[
                  {
                    label: 'Active Teams',
                    value: activeTeams.length,
                    caption: `${teams?.length || 0} total`,
                    icon: <TeamsIcon sx={{ color: '#6366F1' }} />,
                    onClick: () => navigate('/teams'),
                  },
                  {
                    label: 'Bot Responses',
                    value: activitySummary?.bot_response || 0,
                    caption: 'Last 7 days',
                    icon: <BotIcon sx={{ color: '#3B82F6' }} />,
                  },
                  {
                    label: 'Escalations',
                    value: activitySummary?.escalation || 0,
                    caption: 'Last 7 days',
                    icon: <EscalationIcon sx={{ color: '#EF4444' }} />,
                  },
                  {
                    label: 'Pending',
                    value: user?.isAdmin ? (approvalStats?.pending_count || 0) : pendingTeams.length,
                    caption: user?.isAdmin ? 'All teams' : 'Your teams',
                    icon: <PendingIcon sx={{ color: '#D97706' }} />,
                    onClick: () => navigate(user?.isAdmin ? '/admin/teams' : '/teams'),
                  },
                ]}
              />

              <Divider sx={{ my: 1.5 }} />

              <ActivityFeed limit={6} embedded />
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT PANEL: Teams + Quick Actions */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {/* Teams Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    My Teams
                  </Typography>
                  <Chip
                    label={activeTeams.length}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: '#EEF2FF',
                      color: '#6366F1',
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate('/teams')}
                >
                  View all
                </Typography>
              </Box>

              {/* Team List */}
              {teamsLoading ? (
                <Box sx={{ flex: 1 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
                  ))}
                </Box>
              ) : activeTeams.length > 0 ? (
                <List dense disablePadding>
                  {displayTeams.map((team) => (
                    <ListItemButton
                      key={team.id}
                      onClick={() => navigate(`/teams/${team.id}/overview`)}
                      sx={{
                        borderRadius: 1,
                        py: 0.75,
                        px: 1,
                        '&:hover': { backgroundColor: '#F3F4F6' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 0.75,
                            backgroundColor: '#EEF2FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <TeamsIcon sx={{ fontSize: 13, color: '#6366F1' }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={team.name}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      />
                      <ArrowIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No active teams yet</Typography>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              <QuickActionsGrid compact />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
